/**
 * FinanceTile - Net worth, Daily/MTD/YTD changes, liquidity breakdown, budget summary
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { Panel, Spinner } from '../../design/primitives';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';
import { apiClient } from '../../api/apiConfig';

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

interface Driver {
  name: string;
  nw_impact: number;
  is_asset: boolean;
}

interface PeriodChange {
  change: number;
  pct: number;
  drivers: Driver[];
}

interface ChangesData {
  current_net_worth: number;
  daily: PeriodChange | null;
  mtd: PeriodChange | null;
  ytd: PeriodChange | null;
}

interface AccountGroup {
  accounts: any[];
  total: number;
}

interface GroupedData {
  investments: AccountGroup;
  cash: AccountGroup;
  credit_cards: AccountGroup;
  real_estate: AccountGroup;
  vehicles: AccountGroup;
  other_liabilities: AccountGroup;
  businesses: { items: any[]; total: number };
  summary: { total_assets: number; total_liabilities: number; net_worth: number };
}

interface BudgetMonth {
  month: string;
  total_budgeted: number;
  total_spent: number;
}

const FinanceTile: React.FC = () => {
  const [changes, setChanges] = useState<ChangesData | null>(null);
  const [grouped, setGrouped] = useState<GroupedData | null>(null);
  const [budget, setBudget] = useState<BudgetMonth | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [chgRes, grpRes, budRes] = await Promise.all([
          apiClient.get('/api/net-worth/changes/'),
          apiClient.get('/api/financial-accounts/grouped/'),
          apiClient.get('/api/budgets/monthly_summary/?months=1'),
        ]);
        setChanges(chgRes.data);
        setGrouped(grpRes.data);
        const months = budRes.data as BudgetMonth[];
        if (months.length > 0) setBudget(months[months.length - 1]);
      } catch (err) {
        console.error('Failed to load finance tile', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <Panel style={{ minHeight: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size="md" />
      </Panel>
    );
  }

  if (!changes || !grouped) return null;

  const nw = changes.current_net_worth;

  // Liquidity tiers
  const bal = (a: any) => Number(a.current_balance) || 0;
  const cashTotal = Number(grouped.cash.total) || 0;
  const liquidInv = grouped.investments.accounts
    .filter((a: any) => a.account_type !== 'retirement')
    .reduce((s: number, a: any) => s + bal(a), 0);
  const retirement = grouped.investments.accounts
    .filter((a: any) => a.account_type === 'retirement')
    .reduce((s: number, a: any) => s + bal(a), 0);
  const reEquity = grouped.real_estate.accounts
    .reduce((s: number, a: any) => s + (Number(a.linked_loan?.equity) || bal(a)), 0);
  const vehEquity = grouped.vehicles.accounts
    .reduce((s: number, a: any) => s + (Number(a.linked_loan?.equity) || bal(a)), 0);
  const illiquid = reEquity + vehEquity + (Number(grouped.businesses.total) || 0);

  const tiers = [
    { label: 'Cash', value: cashTotal, color: COLORS.status.success },
    { label: 'Investments', value: liquidInv, color: COLORS.accent.default },
    { label: 'Retirement', value: retirement, color: COLORS.status.info },
    { label: 'Illiquid', value: illiquid, color: COLORS.status.warning },
  ];
  const totalPositive = tiers.reduce((s, t) => s + Math.max(0, t.value), 0);

  // Drivers from daily
  const driverPeriod = changes.daily || changes.mtd || changes.ytd;

  // Budget
  const budgetPct = budget && budget.total_budgeted > 0
    ? Math.round((budget.total_spent / budget.total_budgeted) * 100)
    : null;

  const renderChange = (label: string, period: PeriodChange | null) => {
    if (!period) return (
      <div style={{ flex: 1, textAlign: 'center' }}>
        <div style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.tertiary }}>{label}</div>
        <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.tertiary }}>--</div>
      </div>
    );
    const pos = period.change >= 0;
    const color = pos ? COLORS.status.success : COLORS.status.error;
    const arrow = pos ? '\u25B2' : '\u25BC';
    return (
      <div style={{ flex: 1, textAlign: 'center' }}>
        <div style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.tertiary }}>{label}</div>
        <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: '600', color }}>
          {arrow} {formatCurrency(Math.abs(period.change))}
        </div>
        <div style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color, fontWeight: '500' }}>
          {pos ? '+' : ''}{period.pct.toFixed(2)}%
        </div>
      </div>
    );
  };

  return (
    <Panel>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[12] }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
          <CurrencyDollarIcon style={{ width: '18px', height: '18px', color: COLORS.status.success }} />
          <span style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h3,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            Finance
          </span>
        </div>
        <Link to="/finance" style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.accent.default, textDecoration: 'none' }}>
          View All
        </Link>
      </div>

      {/* Net Worth */}
      <div style={{ marginBottom: SPACING[12] }}>
        <div style={{
          fontFamily: TYPOGRAPHY.headings.fontFamily,
          fontSize: TYPOGRAPHY.headings.sizes.h2,
          fontWeight: '700',
          color: COLORS.text.primary,
        }}>
          {formatCurrency(nw)}
        </div>
        <div style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.tertiary }}>
          Net Worth
        </div>
      </div>

      {/* Daily / MTD / YTD */}
      <div style={{
        display: 'flex',
        gap: SPACING[4],
        marginBottom: SPACING[12],
        paddingBottom: SPACING[12],
        borderBottom: `1px solid ${COLORS.border.default}`,
      }}>
        {renderChange('Daily', changes.daily)}
        {renderChange('MTD', changes.mtd)}
        {renderChange('YTD', changes.ytd)}
      </div>

      {/* Drivers */}
      {driverPeriod && driverPeriod.drivers.length > 0 && (
        <div style={{ marginBottom: SPACING[12], paddingBottom: SPACING[12], borderBottom: `1px solid ${COLORS.border.default}` }}>
          <div style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.tertiary, marginBottom: SPACING[4] }}>
            Drivers
          </div>
          {driverPeriod.drivers.slice(0, 3).map((d, i) => {
            const pos = d.nw_impact >= 0;
            const color = pos ? COLORS.status.success : COLORS.status.error;
            return (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: `${SPACING[4]} 0`, fontSize: TYPOGRAPHY.body.sizes.small,
              }}>
                <span style={{ color: COLORS.text.secondary }}>{d.name}</span>
                <span style={{ fontWeight: '600', color }}>{pos ? '+' : ''}{formatCurrency(d.nw_impact)}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Liquidity Breakdown */}
      <div style={{ marginBottom: SPACING[12], paddingBottom: SPACING[12], borderBottom: `1px solid ${COLORS.border.default}` }}>
        <div style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.tertiary, marginBottom: SPACING[8] }}>
          Liquidity
        </div>
        {/* Stacked bar */}
        {totalPositive > 0 && (
          <div style={{
            display: 'flex', height: '8px', borderRadius: RADIUS,
            overflow: 'hidden', marginBottom: SPACING[8],
          }}>
            {tiers.filter(t => t.value > 0).map(t => (
              <div key={t.label} style={{
                width: `${(t.value / totalPositive) * 100}%`,
                backgroundColor: t.color, minWidth: '2px',
              }} />
            ))}
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: `${SPACING[4]} ${SPACING[12]}` }}>
          {tiers.map(t => (
            <div key={t.label} style={{ display: 'flex', alignItems: 'center', gap: SPACING[4], fontSize: TYPOGRAPHY.body.sizes.tiny }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: t.color }} />
              <span style={{ color: COLORS.text.tertiary }}>{t.label}</span>
              <span style={{ color: COLORS.text.secondary, fontWeight: '500' }}>{formatCurrency(t.value)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Budget */}
      {budget && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: SPACING[4] }}>
            <span style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.tertiary }}>
              Budget ({new Date(budget.month + '-01').toLocaleDateString('en-US', { month: 'short' })})
            </span>
            {budgetPct !== null && (
              <span style={{
                fontSize: TYPOGRAPHY.body.sizes.tiny, fontWeight: '600',
                color: budgetPct > 100 ? COLORS.status.error : budgetPct > 85 ? COLORS.status.warning : COLORS.status.success,
              }}>
                {budgetPct}% used
              </span>
            )}
          </div>
          {/* Progress bar */}
          <div style={{ height: '6px', borderRadius: RADIUS, backgroundColor: COLORS.bg.hover, overflow: 'hidden', marginBottom: SPACING[4] }}>
            <div style={{
              height: '100%', borderRadius: RADIUS,
              width: `${Math.min(budgetPct || 0, 100)}%`,
              backgroundColor: (budgetPct || 0) > 100 ? COLORS.status.error : (budgetPct || 0) > 85 ? COLORS.status.warning : COLORS.status.success,
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: TYPOGRAPHY.body.sizes.tiny }}>
            <span style={{ color: COLORS.text.secondary }}>{formatCurrency(budget.total_spent)} spent</span>
            <span style={{ color: COLORS.text.tertiary }}>of {formatCurrency(budget.total_budgeted)}</span>
          </div>
        </div>
      )}
    </Panel>
  );
};

export default FinanceTile;
